/**
 * WhatsAppSuggestionsWidget — Widget "Incidents terrain WhatsApp" pour le dashboard COO
 *
 * Affiche le nombre de suggestions WhatsApp en attente + urgentes.
 * Clic → ouvre la vue filtrée des suggestions WhatsApp dans les tickets unifiés.
 *
 * Props:
 *   onNavigateToTickets: () => void  — callback navigation vers la vue tickets
 */
import { useState, useEffect } from "react";
import { Loader2, MessageCircle, AlertTriangle, ChevronRight, ArrowUpRight, CheckCircle } from "lucide-react";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return { Authorization: `Bearer ${token}` };
};

const getTenantHeaders = () => {
  const tenant = localStorage.getItem("current_tenant") || "";
  return { ...getAuthHeaders(), ...enteteTenant(tenant) };
};

export default function WhatsAppSuggestionsWidget({ onNavigateToTickets }) {
  const [counts, setCounts] = useState({ total: 0, urgent: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getTenantHeaders();
        const [countRes, listRes] = await Promise.all([
          fetch(`${API}/api/unified-tickets/suggestions/count?source=whatsapp`, { headers }),
          fetch(`${API}/api/unified-tickets/suggestions?source=whatsapp&status=pending&limit=3`, { headers }),
        ]);
        const countData = await countRes.json();
        const listData = await listRes.json();
        setCounts({ total: countData.total || 0, urgent: countData.urgent || 0 });
        setRecent((listData.suggestions || []).slice(0, 3));
      } catch {
        setCounts({ total: 0, urgent: 0 });
        setRecent([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const hasUrgent = counts.urgent > 0;

  return (
    <div
      data-testid="whatsapp-suggestions-widget"
      className="rounded-xl overflow-hidden bg-zinc-900/50 border border-white/10"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            hasUrgent ? "bg-red-500/20" : "bg-emerald-500/20"
          }`}>
            <MessageCircle className={`w-3.5 h-3.5 ${
              hasUrgent ? "text-red-400" : "text-emerald-400"
            }`} />
          </div>
          <span className="text-sm font-medium text-white">Incidents terrain</span>
        </div>
        {onNavigateToTickets && (
          <button
            data-testid="whatsapp-widget-see-all"
            onClick={onNavigateToTickets}
            className="text-xs flex items-center gap-1 text-zinc-500 hover:text-white transition-colors"
          >
            Voir tous <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Counters */}
      <div className="px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
          </div>
        ) : counts.total === 0 ? (
          <div className="flex items-center gap-3 py-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <p className="text-sm text-zinc-400">Aucune alerte en attente</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex-1">
                <p className="text-2xl font-bold text-white">{counts.total}</p>
                <p className="text-xs text-zinc-500">
                  suggestion{counts.total > 1 ? "s" : ""} WhatsApp
                </p>
              </div>
              {counts.urgent > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-sm font-semibold text-red-400">{counts.urgent}</span>
                  <span className="text-xs text-red-400/70">urgente{counts.urgent > 1 ? "s" : ""}</span>
                </div>
              )}
            </div>

            {/* Recent list */}
            {recent.length > 0 && (
              <div className="space-y-1.5">
                {recent.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`wa-suggestion-${s.id}`}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={onNavigateToTickets}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      s.suggested_priority === "critical" || s.suggested_priority === "high"
                        ? "bg-red-400"
                        : "bg-amber-400"
                    }`} />
                    <p className="text-xs text-zinc-300 truncate flex-1">
                      {s.suggested_title?.replace("[WhatsApp] ", "") || "Suggestion"}
                    </p>
                    <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
